import { DraggableProvidedDragHandleProps } from '@hello-pangea/dnd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GetPriorityColor } from '../../../../constants/colors';
import { useTodos } from '../../../../context/TodoContext/TodoContext';
import { Todo } from '../../../../types/types';
import TodoPopupMenu from '../../../PopupMenu/TodoPopupMenu';
import { TodoContainer } from '../../style/styles';
import TodoActions from './TodoActions';
import TodoCheckbox from './TodoCheckbox';
import TodoContent from './TodoContent';

interface TodoItemContentProps {
    id: string;
    workspaceId: string | undefined;
    dragHandleProps?: DraggableProvidedDragHandleProps;
}

const TodoItemContent: React.FC<TodoItemContentProps> = ({ id, workspaceId, dragHandleProps, }) => {
    const { deleteTodo, editTodo, todosMap } = useTodos();
    const todo = todosMap.get(id);
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(todo?.text ?? '');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (isEditing && inputRef.current) {
            const input = inputRef.current;
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
        }
    }, [isEditing]);

    if (!todo) return null;

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        if (isEditing) {
            const updatedTodo: Todo = {
                ...todo,
                text: editText.trim() ? editText : "-",
                updatedAt: dayjs()
            };
            editTodo(todo.id, updatedTodo);
            setIsEditing(false);
        } else {
            setIsEditing(true);
        }
        handleMenuClose();
    };

    const handleDelete = (e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        e.preventDefault();
        e.stopPropagation();
        handleMenuClose();
        deleteTodo(todo.id);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (editText.trim()) {
                const updatedTodo: Todo = {
                    ...todo,
                    text: editText,
                    updatedAt: dayjs()
                };
                editTodo(todo.id, updatedTodo);
                setIsEditing(false);
            }
        }
    };

    const handleBlur = () => {
        if (isEditing && editText.trim()) {
            const updatedTodo: Todo = {
                ...todo,
                text: editText,
                updatedAt: dayjs()
            };
            editTodo(todo.id, updatedTodo);
            setIsEditing(false);
        }
    };

    const handleContainerDoubleClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
    };

    const handleContainerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/todo/${workspaceId}/${todo.id}`);
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };


    return (
        <TodoContainer
            {...dragHandleProps}
            className="todo-item"
            isEditing={isEditing}
            onClick={handleContainerClick}
            onDoubleClick={handleContainerDoubleClick}
            onContextMenu={handleMenuOpen}
            sx={{
                borderLeft: '4px solid',
                borderLeftColor: (theme) => {
                    if (todo.completed)
                        return theme.palette.divider;
                    return GetPriorityColor(theme)[todo.priority];
                },
            }}
        >
            <TodoCheckbox checked={todo.completed} id={todo.id} />
            <TodoContent
                todo={todo}
                isEditing={isEditing}
                editText={editText}
                onEditTextChange={setEditText}
                onKeyPress={handleKeyPress}
                onBlur={handleBlur}
                inputRef={inputRef}
                onContextMenu={handleMenuOpen}
            />
            <TodoActions
                isEditing={isEditing}
                onEdit={handleEdit}
            />

            <TodoPopupMenu
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />
        </TodoContainer>
    );
};

export default TodoItemContent; 